/**
 * Правка названия и заметки объекта — там, где объект показан.
 *
 * Одна на всю консоль: правка живёт рядом с вещью, а не в отдельном режиме.
 * Отдельный редактор становится вторым местом, где хранится правда, и рано или
 * поздно расходится с первым.
 *
 * Имя с машины при этом НЕ переписывается. Оно факт: так объект зовётся в
 * `docker ps`, в консоли гипервизора, в отчёте агента. Владелец добавляет своё
 * поверх — и видит оба, иначе объект, названный «Астериском», не найти на
 * машине, где он `vm-pbx`.
 */
import React, { useEffect, useRef, useState } from 'react';

export interface EditNameProps {
  /** Ключ объекта в инвентаре. */
  objectKey: string;
  /** Что показано сейчас: имя владельца или, если его нет, имя с машины. */
  shown: string;
  /** Имя с машины — если владелец назвал объект иначе. */
  factName?: string;
  note?: string;
  busy?: boolean;
  onSave: (objectKey: string, label: string, note: string) => Promise<void> | void;
}

export function EditName({
  objectKey, shown, factName, note, busy, onSave,
}: EditNameProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState(factName ? shown : '');
  const [text, setText] = useState(note ?? '');
  const field = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) field.current?.focus();
  }, [open]);

  if (!open) {
    return (
      <span className="named">
        <span className="named__name">{shown}</span>
        {/*
          Имя с машины рядом, а не в подсказке: подсказку не видно, пока в неё
          не попадёшь мышью, а искать объект на машине приходится именно по
          этому имени.
        */}
        {factName && (
          <span className="named__fact mono" title="Так объект зовётся на машине">
            {factName}
          </span>
        )}
        {note && <span className="named__note" title={note}>✎</span>}
        <button
          type="button"
          className="named__edit"
          disabled={busy}
          onClick={() => setOpen(true)}
          title="Как называть в консоли и что помнить об объекте"
          aria-label={`Переименовать ${shown}`}
        >
          ✎
        </button>
      </span>
    );
  }

  const save = async (): Promise<void> => {
    await onSave(objectKey, label, text);
    setOpen(false);
  };

  return (
    <span className="named named--editing">
      <span className="named__row">
        <span className="named__label">на машине</span>
        <span className="named__fact mono">{factName ?? shown}</span>
      </span>
      <span className="named__row">
        <span className="named__label">называть</span>
        <input
          ref={field}
          className="input input--small"
          value={label}
          maxLength={200}
          placeholder={factName ?? shown}
          disabled={busy}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void save();
            if (e.key === 'Escape') setOpen(false);
          }}
        />
      </span>
      <span className="named__row">
        <span className="named__label">заметка</span>
        <input
          className="input input--small"
          value={text}
          maxLength={2000}
          placeholder="зачем он нужен, что важно помнить"
          disabled={busy}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void save();
            if (e.key === 'Escape') setOpen(false);
          }}
        />
      </span>
      <span className="named__actions">
        {/*
          Пустое поле «называть» — это «вернуть имя с машины», а не «назвать
          пустотой». Так и написано, чтобы стереть было не страшно.
        */}
        <span className="muted named__hint">пустое поле вернёт имя с машины</span>
        <button type="button" className="button button--ghost button--small"
                disabled={busy} onClick={() => setOpen(false)}>
          Отмена
        </button>
        <button type="button" className="button button--small"
                disabled={busy} onClick={() => void save()}>
          Сохранить
        </button>
      </span>
    </span>
  );
}
